/**
 * Choosing a place (Sprint 103.0, 103.1; mockup 7): the device's position, or
 * an address, a town or a place found as you type, or coordinates typed in the
 * same box. Only the typed text is sent, from three letters (ADR-0120 Rule 3).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { degrees } from '../model/format.ts';
import type { Place } from '../model/sky.ts';
import {
  MIN_QUERY_LENGTH,
  parseCoordinates,
  searchPlaces,
  type PlaceHit
} from '../net/geocoder.ts';
import { BackButton, Icon, LanguageSwitch, Notice, useWords } from './common.tsx';

/** How long typing must pause before the text is sent. */
const DEBOUNCE_MS = 300;

type Search =
  | { readonly state: 'idle' }
  | { readonly state: 'searching' }
  | { readonly state: 'done'; readonly hits: readonly PlaceHit[] }
  | { readonly state: 'failed' };

interface Option {
  readonly key: string;
  readonly name: string;
  readonly detail: string;
  readonly source?: string;
  readonly place: Place;
}

function fromHit(hit: PlaceHit): Place {
  return {
    latitude: hit.latitude,
    longitude: hit.longitude,
    source: 'manual',
    label: hit.detail === '' ? hit.name : `${hit.name}, ${hit.detail}`,
    ...(hit.zone === undefined ? {} : { zone: hit.zone })
  };
}

export function LocationScreen({
  initial,
  unavailable,
  onUse,
  onRetry,
  onBack
}: {
  readonly initial: Place | undefined;
  readonly unavailable: boolean;
  readonly onUse: (place: Place) => void;
  readonly onRetry: () => void;
  readonly onBack: () => void;
}): ReactNode {
  const { t, language } = useWords();
  const [query, setQuery] = useState('');
  const [found, setSearch] = useState<Search>({ state: 'idle' });
  const [chosen, setChosen] = useState<Place | undefined>(
    initial?.source === 'manual' ? initial : undefined
  );
  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const typed = parseCoordinates(query);
  const wantsSearch = typed === undefined && query.trim().length >= MIN_QUERY_LENGTH;
  // What a shorter text, or coordinates, left behind is not shown.
  const search: Search = wantsSearch ? found : { state: 'idle' };

  useEffect(() => {
    if (!wantsSearch) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearch({ state: 'searching' });
      void searchPlaces(query, languageRef.current, controller.signal).then(
        (result) => {
          if (!controller.signal.aborted) {
            setSearch(result.failed ? { state: 'failed' } : { state: 'done', hits: result.hits });
          }
        },
        () => {
          if (!controller.signal.aborted) setSearch({ state: 'failed' });
        }
      );
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, wantsSearch]);

  const coordinatesText = (place: { latitude: number; longitude: number }): string =>
    t('location.coordinates', {
      latitude: degrees(place.latitude, language, 4),
      longitude: degrees(place.longitude, language, 4)
    });

  const options: Option[] =
    typed !== undefined
      ? [
          {
            key: 'typed',
            name: t('location.typed'),
            detail: coordinatesText(typed),
            place: { ...typed, source: 'manual' }
          }
        ]
      : search.state === 'done'
        ? search.hits.map((hit, index) => ({
            key: `${index}-${hit.latitude}-${hit.longitude}`,
            name: hit.name,
            detail: hit.detail,
            source: hit.source,
            place: fromHit(hit)
          }))
        : [];
  const same = (a: Place | undefined, b: Place): boolean =>
    a !== undefined && a.latitude === b.latitude && a.longitude === b.longitude;

  return (
    <main className="screen">
      <div className="row">
        <BackButton onClick={onBack} />
        <div className="spacer" />
        <LanguageSwitch />
      </div>
      <h1 className="title">{t('location.title')}</h1>
      {unavailable ? <Notice>{t('location.unavailable')}</Notice> : null}

      <button
        type="button"
        className="btn btn--block"
        style={{ justifyContent: 'flex-start' }}
        onClick={onRetry}
      >
        <Icon name="locate" size={18} />
        {t('location.mine')}
      </button>

      <form
        role="search"
        className="search"
        onSubmit={(event) => {
          event.preventDefault();
          const first = options[0];
          if (first !== undefined) setChosen(first.place);
        }}
      >
        <label htmlFor="place-query" className="visually-hidden">
          {t('location.search')}
        </label>
        <Icon name="search" size={18} />
        <input
          id="place-query"
          className="input"
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('location.search')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </form>
      <p className="note" style={{ marginTop: '-0.25rem' }}>
        {t('location.privacy')}
      </p>

      {search.state === 'searching' ? (
        <p className="note" role="status">
          {t('location.searching')}
        </p>
      ) : null}
      {search.state === 'failed' ? (
        <p className="note" role="alert" style={{ color: 'var(--color-danger)' }}>
          {t('location.failed')}
        </p>
      ) : null}
      {search.state === 'done' && options.length === 0 ? (
        <p className="note" role="status">
          {t('location.none')}
        </p>
      ) : null}
      {options.length > 0 ? (
        <div className="card results" role="group" aria-label={t('location.results')}>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              className="result"
              aria-pressed={same(chosen, option.place)}
              onClick={() => setChosen(option.place)}
            >
              <Icon name="pin" size={18} />
              <span className="result__text">
                <strong>{option.name}</strong>
                {option.detail === '' ? null : <span>{option.detail}</span>}
              </span>
              {option.source === undefined ? null : (
                <span className="result__source">{option.source}</span>
              )}
            </button>
          ))}
        </div>
      ) : null}

      {chosen === undefined ? null : (
        <div className="card chosen">
          <p className="section" style={{ margin: 0 }}>
            {t('location.chosen')}
          </p>
          <strong>{chosen.label ?? t('location.typed')}</strong>
          <span>{coordinatesText(chosen)}</span>
        </div>
      )}

      <div className="spacer" />
      <p className="note" style={{ textAlign: 'center', fontSize: '0.6875rem' }}>
        {t('location.credits')}
      </p>
      <button
        type="button"
        className="btn btn--primary btn--block"
        disabled={chosen === undefined}
        onClick={() => {
          if (chosen !== undefined) onUse(chosen);
        }}
      >
        {t('location.use')}
      </button>
    </main>
  );
}
